import { redirectSignIn, redirectSignInByTicket, fxboTokenSignIn, getSession } from "@/api/user";
import { getLanguagesApi } from "@/api/common";
import React, { useEffect, useRef, useState } from "react";
import { IUser } from "@/props/IUser";
import { setCookie, getCookie } from "cookies-next";
import { useRouter } from "next/router";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setToken, setUserInfo } from "@/store/user";
import { mutate, useSWRConfig } from "swr";
import { setAuthBootstrapping } from "@/utils/authBootstrap";
import { cookieOptions, setUserInfoCookie } from "@/utils/cookie";
import { safeRedirectPath, isBasePathAbsolute } from "@/utils/safeRedirect";
import { consumeHandOff } from "@/utils/crossSiteHandoff";
import BASE_PATH from "@/config/basePath";
import nextI18NextConfig from "@/next-i18next.config";

/**
 * FXBO 嵌入页（积分商城 widget）跳过来时，CMS JWT 放在 #fxboToken（# 不发往服务端、不进 Referer 和访问日志）。
 * 模块加载时就取走、抹掉地址栏并进入登录引导态：等 effect 再做就晚了，
 * 落地页一挂载发出的请求 401 会先把人踢去 /login（effect 子组件先于 Layout 执行）
 */
const takeFxboToken = (): string => {
  if (typeof window === "undefined") return "";
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const jwt = hash.get("fxboToken");
  if (!jwt) return "";
  hash.delete("fxboToken");
  const rest = hash.toString();
  // 保留 history.state，否则 Next router 后退会出错
  window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search + (rest ? `#${rest}` : ""));
  setAuthBootstrapping(true);
  // 用 widget 带过来的语言（/pointsmall/<lang>/...）：写进 NEXT_LOCALE，Languages 和 resolveLocale 都是 cookie 优先，
  // 后端语言列表里没有就照常回退列表第一个。不写的话会被用户之前存的 cookie 改成别的语言
  const lang = window.location.pathname.slice(BASE_PATH.length).split("/")[1];
  if (nextI18NextConfig.locales.includes(lang)) {
    setCookie("NEXT_LOCALE", lang, { ...cookieOptions(), maxAge: 60 * 60 * 24 * 365 });
  }
  return jwt;
};
let pendingFxboToken = takeFxboToken();

const RedirectSignIn = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { cache } = useSWRConfig();
  const site = useAppSelector((state) => state.app.site);
  const [isProcessing, setIsProcessing] = useState(false);
  // 同步去重：state 是异步提交的，effect 在 state 提交前可能因 router.query 变化再次运行，
  // 用 ref 记录已处理的参数组合，第二次运行能立刻看到，避免 appToken 一次性 ticket 被重复换票。
  const handledRef = useRef<string | null>(null);

  // 登录完成后清理 URL 上的参数：只移除登录相关的敏感参数，
  // 保留 hideBack 等页面展示参数（App 内嵌场景需要它持续生效）
  // 用 router（shallow，不重新取数）而不是 history.replaceState：后者只改地址栏，router.query 里仍留着 appToken，
  // 之后切优惠券标签 / 翻页 / 切语言（都用 {...router.query} 跳转）会把它带回地址栏，刷新就拿用过的票再换一次
  const cleanUrlQuery = () => {
    const restQuery = { ...router.query };
    ["appToken", "token", "udid", "user", "redirect", "_fromGeo"].forEach((key) => delete restQuery[key]);
    router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
  };

  // 登录后重拉本页数据。mutate(() => true) 会跳过 useSWRInfinite 的 $inf$ key（SWR 2.x 有意为之），
  // 移动端优惠券等列表用的正是它，要逐个单独刷，否则登录前抢跑失败的那页一直是空的
  const revalidateAll = () => {
    mutate(() => true);
    Array.from(cache.keys())
      .filter((key) => key.startsWith("$inf$"))
      .forEach((key) => mutate(key));
  };

  // 免登/跳转登录落地后，按后端 languages/list 决定应显示的语言并让其"真正生效"。
  // 关键：Languages 组件在免登流程里会跳过语言纠正（见其 inLoginFlow），落地后也不会再触发；
  // 改语言又只能靠 Next 路由跳转，故必须在这里用 router 切 locale：
  // cookie 优先（尊重用户已选），否则取后端列表第一个 arr[0]。
  const resolveLocale = async () => {
    const cur = (router.locale || "en").toLowerCase();
    let target = cur;
    try {
      const res: any = await getLanguagesApi({ showCNLanguages: site === "cn" ? "y" : "n" });
      const arr: string[] = Array.isArray(res) ? res.map((i: any) => i.language.toLowerCase()) : [];
      const cookieLoc = (getCookie("NEXT_LOCALE") || "").toString().toLowerCase();
      if (cookieLoc && arr.indexOf(cookieLoc) >= 0) target = cookieLoc;
      else if (arr.length > 0) target = arr[0];
    } catch (e) {
      console.error("resolve locale after sign in failed:", e);
    }
    if (target && target !== cur) {
      setCookie("NEXT_LOCALE", target, { ...cookieOptions(), maxAge: 60 * 60 * 24 * 365 });
    }
    return { cur, target };
  };

  const applyLocaleThenClean = async () => {
    const { cur, target } = await resolveLocale();

    if (target && target !== cur) {
      // 保留 hideBack 等展示参数，仅剔除登录敏感参数；用 router 跳转让 locale 真正切换
      const restQuery = { ...router.query };
      ["appToken", "token", "udid", "user", "redirect", "_fromGeo"].forEach((k) => delete restQuery[k]);
      // 切完语言同样要重拉本页数据：落地页抢跑的请求没带 token 已失败，SWR key 不含 token 不会自己重试
      router
        .replace({ pathname: router.pathname, query: restQuery }, undefined, { locale: target })
        .then(revalidateAll);
    } else {
      // 语言无需变更：只做 shallow 清理，不触发整页 SSR 重新取数
      cleanUrlQuery();
      // 停留本页：登录前无 token 的请求已失败/为空，登录成功后重新拉取本页数据
      revalidateAll();
    }
  };

  // 登录成功后按 redirect 参数跳转到目标页（如 /user/points、/user/voucher）；
  // 没有目标则做语言对齐并清理 URL 参数、留在当前页。
  // 跨站登录时（DetectionIp 跳到对应站点），目标路径通过 URL 的 redirect 参数带过来，这里同样生效。
  const redirectAfterSignIn = async () => {
    const rawRedirect = router.query.redirect;
    if (typeof rawRedirect !== "string" || !rawRedirect) {
      applyLocaleThenClean();
      return;
    }
    // redirect 外部可控，先过校验再用（见 utils/safeRedirect.ts）
    const redirect = safeRedirectPath(rawRedirect);
    // 完整路径（带 basePath /pointsmall）整页跳；纯路径用 router 跳，自动补 basePath + 当前 locale
    if (isBasePathAbsolute(redirect)) {
      window.location.href = redirect;
    } else {
      // 同样要对齐语言：Languages 只在挂载时纠正一次，从 /login 中转过来时它已跳过（IP 入口跨站落到受保护页就是这条路）。
      // redirect 自带语言前缀（如 /sc/user/voucher）时照旧跳，再指定 locale 会拼成 /tc/sc/...
      const firstSeg = redirect.split("?")[0].split("/")[1] || "";
      if (router.locales?.includes(firstSeg)) {
        router.replace(redirect);
      } else {
        const { target } = await resolveLocale();
        router.replace(redirect, undefined, { locale: target });
      }
    }
  };

  /**
   * crm jump sign in
   * @param token
   */
  const handleCrmSignIn = async (token: string) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setAuthBootstrapping(true);
    try {
      const user: IUser | any = await redirectSignIn({
        token: token,
        lang: "",
      });
      setCookie("user", JSON.stringify(user), cookieOptions());
      setCookie("token", user.bssToken, cookieOptions());
      dispatch(setToken(user.bssToken));
      const userInfo: any = await getSession();
      // 脏值防御（getSession 被 401 拦截器吞掉时返回 undefined）已收进 setUserInfoCookie
      setUserInfoCookie(userInfo);
      dispatch(setUserInfo(userInfo));
      redirectAfterSignIn();
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
      cleanUrlQuery();
    } finally {
      setAuthBootstrapping(false);
      setIsProcessing(false);
    }
  };

  /**
   * site jump sign in
   * @param token
   */
  const handleSiteJumpSignIn = async (token: string, user: string) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setAuthBootstrapping(true);
    try {
      await setCookie("token", token, cookieOptions());
      await setCookie("user", user, cookieOptions());
      dispatch(setToken(token));
      const userInfo: any = await getSession();
      // 脏值防御（getSession 被 401 拦截器吞掉时返回 undefined）已收进 setUserInfoCookie
      setUserInfoCookie(userInfo);
      dispatch(setUserInfo(userInfo));
      redirectAfterSignIn();
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
      cleanUrlQuery();
    } finally {
      setAuthBootstrapping(false);
      setIsProcessing(false);
    }
  };

  /**
   * App 跳转登录（appToken → redirectSignInByTicket）
   * @param appToken App 传入的 ticket
   */
  const handleAppSignIn = async (appToken: string) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setAuthBootstrapping(true);

    // 单独捕获换票接口的错误：token 失效/用户不存在等。拦截器已按错误码弹出对应提示，这里只做收尾。
    let user: IUser | any;
    try {
      user = await redirectSignInByTicket(appToken);
    } catch (errorInfo) {
      console.error("redirectSignInByTicket failed:", errorInfo);
      setAuthBootstrapping(false);
      setIsProcessing(false);
      cleanUrlQuery();
      return;
    }

    try {
      const finalToken = user.bssToken;
      setCookie("user", JSON.stringify(user), cookieOptions());
      setCookie("token", finalToken, cookieOptions());
      dispatch(setToken(finalToken));
      const userInfo: any = await getSession();
      // 脏值防御（getSession 被 401 拦截器吞掉时返回 undefined）已收进 setUserInfoCookie
      setUserInfoCookie(userInfo);
      dispatch(setUserInfo(userInfo));
      redirectAfterSignIn();
    } catch (errorInfo) {
      console.error("App sign in failed:", errorInfo);
      cleanUrlQuery();
    } finally {
      setAuthBootstrapping(false);
      setIsProcessing(false);
    }
  };

  /**
   * FXBO 嵌入页跳转登录（#fxboToken 的 CMS JWT → fxboTokenSignIn → 商城 token）
   * @param jwt CMS 签发的 JWT，不是商城 token
   */
  const handleFxboTokenSignIn = async (jwt: string) => {
    setIsProcessing(true);
    let user: IUser | any;
    try {
      user = await fxboTokenSignIn(jwt);
    } catch (errorInfo) {
      // JWT 失效（2001）/ 商城无此用户（2005）：拦截器已弹提示，按访客继续浏览
      console.error("fxboTokenSignIn failed:", errorInfo);
      setAuthBootstrapping(false);
      setIsProcessing(false);
      return;
    }

    try {
      setCookie("user", JSON.stringify(user), cookieOptions());
      setCookie("token", user.fxboToken, cookieOptions());
      dispatch(setToken(user.fxboToken));
      const userInfo: any = await getSession();
      setUserInfoCookie(userInfo);
      dispatch(setUserInfo(userInfo));
      redirectAfterSignIn();
    } catch (errorInfo) {
      console.error("FXBO sign in failed:", errorInfo);
      cleanUrlQuery();
    } finally {
      setAuthBootstrapping(false);
      setIsProcessing(false);
    }
  };

  /**
   * fxbo jump sign in
   * @param fxboUuid
   */
  // const handleFxboJumpSignIn = async (fxboUuid: string) => {
  //   try {
  //     const res: any = await fxboSignIn({ fxboUuid });
  //     const { token } = res;
  //     if (!token) return;
  //     handleSiteJumpSignIn(token);
  //   } catch (errorInfo) {
  //     console.log("Failed:", errorInfo);
  //   }
  // };

  useEffect(() => {
    if (!router.isReady) return;

    // FXBO 嵌入页跳转登录：# 在模块加载时已取走，只处理一次
    if (pendingFxboToken) {
      const jwt = pendingFxboToken;
      pendingFxboToken = "";
      handleFxboTokenSignIn(jwt);
      return;
    }

    // app jump login
    const appToken: string | string[] | undefined = router.query.appToken;

    // crm secret login
    const token: string | string[] | undefined = router.query.token;

    // ⚡ 用 queryKey 标记当前参数组合，同一组参数只处理一次（ref 同步，防重复换票）
    const queryKey = JSON.stringify({ appToken, token });
    if (handledRef.current === queryKey) return;

    // App 跳转登录（优先级最高）
    if (appToken && typeof appToken === "string") {
      handledRef.current = queryKey; // 先同步置位，杜绝第二次 effect 再次进入
      handleAppSignIn(appToken);
      return;
    }

    if (token && typeof token === "string") {
      handledRef.current = queryKey;
      handleCrmSignIn(token);
      return;
    }

    // site jump login：登录态由交接 cookie / # 带来，不再从查询参数读（VULN-031）。
    // 取后即删，同一次交接不会被处理两次；受保护页被中间件转到 /login 时 # 会跟过去，登录页同样走这里
    const handedOff = consumeHandOff();
    if (handedOff) {
      handledRef.current = queryKey;
      handleSiteJumpSignIn(handedOff.token, handedOff.user);
    }

    // fxbo jump login 不再使用，有安全隐患
    // const fxboUuid: string | string[] | undefined = router.query.fxboUuid;
    // if (fxboUuid && typeof fxboUuid === "string") {
    //   handleFxboJumpSignIn(fxboUuid);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query]);

  return <></>;
};

export default RedirectSignIn;
